/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  AlertsAPIResponse,
  AlertsInitialProperties,
  RequestAlertsInterface,
} from '../../interface/alerts-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import stationManagementApi from '../../utils/api/station-management';

// a thunk to fetch users
export const fetchAlerts = createAsyncThunk<AlertsAPIResponse, RequestAlertsInterface>(
  'alerts/fetchAlerts',
  async (data: RequestAlertsInterface, { dispatch }) => {
    try {
      const response = await stationManagementApi.fetchAlertsList(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchAlertsFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  { chargingStation: string }
>('alerts/fetchAlertsFilters', async ({ chargingStation }, { dispatch }) => {
  if (!chargingStation) {
    throw new Error('chargingStation is required');
  }
  try {
    const response = await stationManagementApi.fetchAlertsListFilters({ chargingStation });
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: AlertsInitialProperties = {
  alerts: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateAlertsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableAlertsFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function for sorting data by it's field
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AlertsInitialProperties>) => {
    builder
      .addCase(fetchAlerts.pending, (state: AlertsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAlerts.fulfilled,
        (state: AlertsInitialProperties, action: PayloadAction<AlertsAPIResponse>) => {
          console.log(action.payload);

          state.loading = false;
          state.alerts = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAlerts.rejected, (state: AlertsInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchAlertsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAlertsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload];
        },
      )
      .addCase(fetchAlertsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateAlertsFilters, disableAlertsFiltersApplied, setSortData } =
  alertsSlice.actions;
// Export the reducer
export default alertsSlice.reducer;
